import { useCallback, useState } from 'react';
import { Card, ViewName } from '../types';

export interface ViewNavState {
  currentView: ViewName;
  activeWB: number | null;
  switchView: (v: ViewName) => void;
  openWorkboard: (cardId: number) => void;
  wbList: () => Card[];
  clearActiveWorkboardIfMatches: (cardId: number) => void;
  remapActiveWorkboard: (oldId: number, newId: number) => void;
}

export const useViewNav = (cards: Card[]): ViewNavState => {
  const [currentView, setCurrentView] = useState<ViewName>('ov');
  const [activeWB, setActiveWB] = useState<number | null>(null);

  const wbList = useCallback(
    (): Card[] =>
      [...cards].sort((a, b) => b.createdAt.getTime() - a.createdAt.getTime()),
    [cards],
  );

  const openWorkboard = useCallback((cardId: number): void => {
    setActiveWB(cardId);
    setCurrentView('wb');
  }, []);

  const switchView = useCallback(
    (v: ViewName): void => {
      if (v === 'wb' && activeWB === null) {
        const list = wbList();
        if (list.length) {
          openWorkboard(list[0].id);
          return;
        }
      }
      setCurrentView(v);
    },
    [activeWB, wbList, openWorkboard],
  );

  const clearActiveWorkboardIfMatches = useCallback((cardId: number): void => {
    setActiveWB(prev => (prev === cardId ? null : prev));
  }, []);

  const remapActiveWorkboard = useCallback(
    (oldId: number, newId: number): void => {
      setActiveWB(prev => (prev === oldId ? newId : prev));
    },
    [],
  );

  return {
    currentView,
    activeWB,
    switchView,
    openWorkboard,
    wbList,
    clearActiveWorkboardIfMatches,
    remapActiveWorkboard,
  };
};
